/**
 * Service pour récupérer les données d'horoscopes depuis le fichier JSON statique
 * Ce service permet d'avoir du contenu disponible au moment du rendu statique (SEO-friendly)
 */

const STATIC_HOROSCOPE_PATH = '/data/latest-horoscope.json';

class StaticHoroscopeService {
    constructor() {
        this.cachedData = null;
        this.lastFetchTime = null;
        this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes en millisecondes
    }

    /**
     * Récupère les données d'horoscopes depuis le fichier JSON statique
     * @returns {Promise<Object>} Les données d'horoscopes
     */
    async getLatestHoroscope() {
        try {
            // Vérification du cache
            if (this.cachedData && this.lastFetchTime && 
                Date.now() - this.lastFetchTime < this.CACHE_DURATION) {
                console.log('📄 Utilisation des données en cache');
                return this.cachedData;
            }

            console.log('📡 Chargement des horoscopes depuis le fichier statique...');
            
            // Ajout d'un paramètre de cache-busting basé sur l'heure
            const cacheBuster = Math.floor(Date.now() / (60 * 60 * 1000)); // Change chaque heure
            const response = await fetch(`${STATIC_HOROSCOPE_PATH}?v=${cacheBuster}`);

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status} - ${response.statusText}`);
            }

            const data = await response.json();

            // Vérification de la structure des données
            if (!data.success) {
                throw new Error(data.error || 'Données d\'horoscope invalides');
            }

            // Mise en cache
            this.cachedData = data;
            this.lastFetchTime = Date.now();

            console.log('✅ Horoscopes chargés depuis le fichier statique');
            return data;

        } catch (error) {
            console.error('❌ Erreur lors du chargement des horoscopes statiques:', error);
            
            // Retour des données en cache si disponibles en cas d'erreur
            if (this.cachedData) {
                console.log('⚠️ Utilisation des données en cache suite à l\'erreur');
                return this.cachedData;
            }

            throw error;
        }
    }

    /**
     * Récupère l'horoscope pour un signe spécifique
     * @param {string} signe - Le nom du signe astrologique
     * @returns {Promise<Object|null>} Les données d'horoscope pour le signe ou null
     */
    async getHoroscopeForSign(signe) {
        try {
            const data = await this.getLatestHoroscope();
            
            if (!data.data || !data.data.horoscopes_data || !data.data.horoscopes_data.horoscopes) {
                return null;
            }

            return data.data.horoscopes_data.horoscopes[signe] || null;
            
        } catch (error) {
            console.error(`❌ Erreur lors de la récupération de l'horoscope pour ${signe}:`, error);
            return null;
        }
    }

    /**
     * Vérifie si les données d'horoscopes sont disponibles
     * @returns {Promise<boolean>} True si les données sont disponibles
     */
    async isHoroscopeAvailable() {
        try {
            const data = await this.getLatestHoroscope();
            return data && data.success && data.data && data.data.horoscopes_data;
        } catch (error) {
            return false;
        }
    }

    /**
     * Récupère la date de génération des horoscopes
     * @returns {Promise<Date|null>} La date de génération ou null
     */
    async getGenerationDate() {
        try {
            const data = await this.getLatestHoroscope();
            
            if (data.data && data.data.timestamp_creation) {
                return new Date(data.data.timestamp_creation * 1000);
            }
            
            return null;
        } catch (error) {
            console.error('❌ Erreur lors de la récupération de la date de génération:', error);
            return null;
        }
    }

    /**
     * Efface le cache des données
     */
    clearCache() {
        this.cachedData = null;
        this.lastFetchTime = null;
        console.log('🗑️ Cache des horoscopes effacé');
    }

    /**
     * Vérifie si les données sont générées automatiquement
     * @returns {Promise<boolean>} True si générées automatiquement
     */
    async isAutomaticallyGenerated() {
        try {
            const data = await this.getLatestHoroscope();
            return data.generated_automatically || false;
        } catch (error) {
            return false;
        }
    }

    /**
     * Récupère les informations de debug sur les données
     * @returns {Promise<Object>} Informations de debug
     */
    async getDebugInfo() {
        try {
            const data = await this.getLatestHoroscope();
            
            return {
                success: data.success,
                hasData: !!data.data,
                lastUpdated: data.last_updated,
                automaticallyGenerated: data.generated_automatically,
                horoscopeId: data.data?.horoscope_id,
                timestampCreation: data.data?.timestamp_creation,
                horoscopesCount: data.data?.horoscopes_data?.horoscopes ? 
                    Object.keys(data.data.horoscopes_data.horoscopes).length : 0,
                cacheAge: this.lastFetchTime ? Date.now() - this.lastFetchTime : null
            };
        } catch (error) {
            return {
                error: error.message,
                hasData: false,
                success: false
            };
        }
    }
}

// Export d'une instance singleton
const staticHoroscopeService = new StaticHoroscopeService();
export default staticHoroscopeService; 